import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Merge, Plus, Search, Star, Trash2, TrendingUp, Undo2, X } from "lucide-react";
import { tradesApi, type Trade, type TradesFilter } from "../lib/api";
import { formatCurrency, formatDate, formatSignedCurrency } from "../lib/format";
import { useI18n } from "../lib/i18n";
import AddTradeForm from "../components/AddTradeForm";
import Select from "../components/Select";

export default function TradesPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<TradesFilter>({ status: "all", pnl: "any", sort: "newest" });
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [mergeError, setMergeError] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ["trades", filter],
    queryFn: () => tradesApi.list(filter),
  });

  const trades = listQ.data?.trades ?? [];

  const selectedTrades = trades.filter((t) => selected.includes(t.id));
  // The server enforces this too; checking here lets the bar explain WHY it is
  // disabled instead of failing after the click.
  const sameSymbol = new Set(selectedTrades.map((t) => t.symbol ?? "")).size <= 1;
  const sameSide = new Set(selectedTrades.map((t) => t.side)).size <= 1;
  const canMerge = selectedTrades.length >= 2 && sameSymbol && sameSide;

  const mergeM = useMutation({
    mutationFn: () => tradesApi.merge(selected),
    onSuccess: () => {
      setSelected([]);
      setMergeError(null);
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["journey"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => setMergeError((e as Error).message),
  });

  function toggle(id: number) {
    setMergeError(null);
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("nav.trades")}</p>
          <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-accent" />
            {t("trades.journal")}
          </h1>
        </div>
        <button type="button" onClick={() => setAddOpen(true)} className="btn-gold">
          <Plus className="w-4 h-4" />
          {t("trades.logTrade")}
        </button>
      </header>

      {/* Filter bar */}
      <section className="glass p-4 flex flex-wrap items-center gap-3">
        <PillGroup
          value={filter.status ?? "all"}
          options={[
            { v: "all", l: t("common.all") },
            { v: "open", l: t("trades.open") },
            { v: "closed", l: t("trades.closed") },
          ]}
          onChange={(v) => setFilter({ ...filter, status: v as TradesFilter["status"] })}
        />
        <PillGroup
          value={filter.pnl ?? "any"}
          options={[
            { v: "any", l: t("trades.pnlAny") },
            { v: "profit", l: t("trades.profit"), cls: "text-pnl-pos" },
            { v: "loss", l: t("trades.loss"), cls: "text-pnl-neg" },
          ]}
          onChange={(v) => setFilter({ ...filter, pnl: v as TradesFilter["pnl"] })}
        />
        <label className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-parchment-muted" />
          <input
            type="text"
            value={filter.search ?? ""}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            placeholder={t("trades.searchPlaceholder")}
            className="input ps-9"
          />
        </label>
        <Select
          value={filter.sort ?? "newest"}
          onChange={(v) => setFilter({ ...filter, sort: v as TradesFilter["sort"] })}
          className="w-auto"
          options={[
            { value: "newest", label: t("trades.sortNewest") },
            { value: "oldest", label: t("trades.sortOldest") },
            { value: "biggest_win", label: t("trades.sortBiggestWin") },
            { value: "biggest_loss", label: t("trades.sortBiggestLoss") },
          ]}
        />
      </section>

      {addOpen && (
        <AddTradeForm
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["trades"] });
            qc.invalidateQueries({ queryKey: ["wallet"] });
            setAddOpen(false);
          }}
        />
      )}

      <section className="glass p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-parchment">
            {listQ.isLoading
              ? t("common.loading")
              : t("trades.countTrades", { count: listQ.data?.meta.total ?? 0 })}
          </h2>
        </div>

        {listQ.isError && (
          <p className="text-pnl-neg text-sm">{(listQ.error as Error)?.message ?? t("trades.error")}</p>
        )}

        {!listQ.isLoading && trades.length === 0 && (
          <p className="text-parchment-muted text-sm py-8 text-center">
            {t("trades.empty")}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {trades.map((t) => (
            <TradeCard
              key={t.id}
              trade={t}
              selected={selected.includes(t.id)}
              onToggle={() => toggle(t.id)}
            />
          ))}
        </div>
      </section>

      {selected.length > 0 && (
        <div className="sticky bottom-4 z-30 flex justify-center">
          <div className="glass shadow-lift px-4 py-3 flex flex-wrap items-center gap-3 max-w-full">
            <span className="text-sm text-parchment font-semibold">
              {t("trades.selectedCount", { count: selected.length })}
            </span>
            <button
              type="button"
              onClick={() => mergeM.mutate()}
              disabled={!canMerge || mergeM.isPending}
              className="btn-gold disabled:opacity-40"
              title={canMerge ? undefined : t("trades.mergeHint")}
            >
              <Merge className="w-4 h-4" />
              {mergeM.isPending ? t("common.saving") : t("trades.mergeBtn")}
            </button>
            <button type="button" onClick={() => { setSelected([]); setMergeError(null); }} className="btn-ghost">
              <X className="w-4 h-4" />
              {t("common.cancel")}
            </button>
            {!canMerge && selected.length >= 2 && (
              <span className="text-xs text-pnl-neg w-full sm:w-auto">{t("trades.mergeHint")}</span>
            )}
            {mergeError && <span className="text-xs text-pnl-neg w-full sm:w-auto">{mergeError}</span>}
          </div>
        </div>
      )}
    </>
  );
}

function PillGroup<V extends string>({
  value,
  options,
  onChange,
}: {
  value: V;
  options: Array<{ v: V; l: string; cls?: string }>;
  onChange: (v: V) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-xl bg-surface-1 border border-glass p-1">
      {options.map((o) => {
        const active = o.v === value;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={
              "rounded-lg px-3 py-1.5 text-xs font-semibold transition " +
              (active
                ? "bg-surface-3 text-parchment ring-1 ring-white/10 " + (o.cls ?? "")
                : "text-parchment-muted hover:bg-surface-2")
            }
          >
            {o.l}
          </button>
        );
      })}
    </div>
  );
}

function TradeCard({
  trade: t,
  selected,
  onToggle,
}: {
  trade: Trade;
  selected: boolean;
  onToggle: () => void;
}) {
  const { t: tr } = useI18n();
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["trades"] });
    qc.invalidateQueries({ queryKey: ["wallet"] });
    qc.invalidateQueries({ queryKey: ["journey"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const destroyM = useMutation({
    mutationFn: () => tradesApi.destroy(t.id),
    onSuccess: invalidate,
  });
  const unmergeM = useMutation({
    mutationFn: () => tradesApi.unmerge(t.id),
    onSuccess: invalidate,
  });
  const isMerged = t.merged_from_count > 0;
  const isOpen = t.status === "open";
  const pnl = t.profit;
  const cls = isOpen
    ? "text-amber-300"
    : pnl > 0
      ? "text-pnl-pos"
      : pnl < 0
        ? "text-pnl-neg"
        : "text-parchment";
  return (
    <div className="glass glass-hover p-4 relative">
      <Link to={`/trades/${t.id}`} className="absolute inset-0 z-10" aria-label={tr("trades.openTradeAria", { id: t.id })} />
      <div className="flex items-start justify-between gap-2 mb-2 relative">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={
              "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
              (t.side === "Buy" ? "bg-emerald-500/15 text-pnl-pos" : "bg-rose-500/15 text-pnl-neg")
            }
          >
            {t.side}
          </span>
          <span className="font-mono font-semibold text-parchment truncate">{t.symbol || "—"}</span>
          {isOpen && <span className="chip-gold text-[10px] px-2 py-0.5">{tr("trades.open")}</span>}
          {isMerged && (
            <span
              className="inline-flex items-center rounded-md border border-glass-mid px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-parchment-muted"
              title={tr("trades.mergedOf", { count: t.merged_from_count })}
            >
              {tr("trades.mergedBadge", { count: t.merged_from_count })}
            </span>
          )}
        </div>
        <label
          className="relative z-20 flex items-center cursor-pointer p-1 -m-1"
          onClick={(e) => e.stopPropagation()}
          title={tr("trades.selectForMerge")}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            aria-label={tr("trades.selectForMerge")}
            className="accent-gold-500 w-4 h-4 cursor-pointer"
          />
        </label>
        {isMerged && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (confirm(tr("trades.confirmUnmerge"))) unmergeM.mutate();
            }}
            disabled={unmergeM.isPending}
            title={tr("trades.unmerge")}
            aria-label={tr("trades.unmerge")}
            className="text-parchment-muted hover:text-accent disabled:opacity-40 relative z-20"
          >
            <Undo2 className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (confirm(tr("trades.confirmDelete"))) destroyM.mutate();
          }}
          disabled={destroyM.isPending}
          className="text-parchment-muted hover:text-pnl-neg disabled:opacity-40 relative z-20"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <p className={`text-2xl font-bold tabular relative ${cls}`}>{formatSignedCurrency(pnl)}</p>
      {/* Both legs with their times: entry is what the trader thinks in, and
          exit tells them how long it ran. formatDate already renders the hour
          in the user's display timezone. */}
      <div className="mt-2 relative text-xs text-parchment-muted grid gap-0.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-[9px] uppercase tracking-wider opacity-70 shrink-0">
              {tr("trades.openedAt")}
            </span>
            <span className="tabular truncate">{formatDate(t.open_time ?? t.close_time)}</span>
          </span>
          <span className="tabular shrink-0">
            {t.balance_after != null ? formatCurrency(t.balance_after) : "—"}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-[9px] uppercase tracking-wider opacity-70 shrink-0">
              {tr("trades.closedAt")}
            </span>
            <span className="tabular truncate">
              {t.close_time ? formatDate(t.close_time) : tr("trades.stillOpen")}
            </span>
          </span>
          {/* Where this trade left the journey. Gold when it moved a level, so
              a promotion (or a demotion) is visible while scanning the list.
              dir=ltr keeps the arrow pointing the right way in RTL locales. */}
          {t.level_before != null && t.level_after != null && (
            <span
              dir="ltr"
              title={tr("trades.levelChange")}
              className={
                "tabular shrink-0 " +
                (t.level_after !== t.level_before ? "text-accent font-semibold" : "")
              }
            >
              L{t.level_before} → L{t.level_after}
            </span>
          )}
        </div>
      </div>
      {(t.stars ?? 0) > 0 && (
        <div className="mt-2 flex gap-0.5 relative">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={
                "w-3 h-3 " +
                (i < (t.stars ?? 0) ? "fill-gold-300 text-accent" : "text-parchment-muted/30")
              }
            />
          ))}
        </div>
      )}
      {t.notes && <p className="mt-2 text-xs text-parchment-muted truncate relative">{t.notes}</p>}
    </div>
  );
}
